import type { UserRole } from '@/entities/auth/model/auth.types';
import { hasAccess } from './roleHierarchy';
import type {
  NavigationItemConfig,
  RouteConfigItem,
  StaticCommandConfig,
  DynamicGeneratorConfig,
} from './workspace-config.types';

/**
 * 페이지 접근(404)용: viewMode 또는 userRole 중 하나라도 허용하면 접근 가능.
 * 플래너 모드여도 userRole이 Operator 이상이면 Operator 페이지 접근 가능.
 */
function canAccessRoute(
  requiredRole: UserRole,
  viewMode: UserRole,
  userRole?: UserRole | null
): boolean {
  if (hasAccess(viewMode, requiredRole)) return true;
  return !!(userRole && hasAccess(userRole, requiredRole));
}

/** 사이드바: viewMode 기준만 (모드에 맞게 메뉴 변경) */
export function filterNavigationByViewMode(
  items: readonly NavigationItemConfig[],
  viewMode: UserRole
): NavigationItemConfig[] {
  return items.filter((item) => {
    if (!hasAccess(viewMode, item.requiredRole)) return false;
    if (item.sidebarHiddenForRoles?.includes(viewMode)) return false;
    // sidebarVisibleForRoles 설정 시 allowlist 검사 (Planner 전용 항목 등)
    if (item.sidebarVisibleForRoles && !item.sidebarVisibleForRoles.includes(viewMode))
      return false;
    return true;
  });
}

/** 라우트(404 판단): viewMode 또는 userRole 허용 시 접근 가능 */
export function filterRoutesByViewMode(
  routes: readonly RouteConfigItem[],
  viewMode: UserRole,
  userRole?: UserRole | null
): RouteConfigItem[] {
  return routes.filter(
    (route) =>
      canAccessRoute(route.requiredRole, viewMode, userRole) ||
      (route.allowPlannerSelf === true && viewMode === 'Planner')
  );
}

/** 커맨드: viewMode 기준만 (모드에 맞게 메뉴 변경) */
export function filterCommandsByViewMode(
  commands: readonly StaticCommandConfig[],
  viewMode: UserRole
): StaticCommandConfig[] {
  return commands.filter((cmd) => hasAccess(viewMode, cmd.requiredRole));
}

/** 동적 커맨드: viewMode 기준만 */
export function filterDynamicGeneratorsByViewMode(
  generators: readonly DynamicGeneratorConfig[] | undefined,
  viewMode: UserRole
): DynamicGeneratorConfig[] {
  if (!generators) return [];
  return generators.filter((g) => g.enabled && hasAccess(viewMode, g.requiredRole));
}
